import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api-client';
import { formatDate, formatMs } from '@/lib/utils';
import { StatusBadge } from '@/components/shared/status-badge';
import type { ConversationDetail, ConversationStatus } from '@/lib/types';

const STATUS_TONE: Record<ConversationStatus, 'green' | 'rose' | 'amber'> = {
  ok: 'green',
  error: 'rose',
  blocked: 'amber',
};

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        {title}
      </div>
      {children}
    </div>
  );
}

function Payload({ text }: { text: string }): JSX.Element {
  return (
    <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded border border-slate-200 bg-slate-50 p-3 font-mono text-[11px] text-slate-700">
      {text || '(üres)'}
    </pre>
  );
}

function prettyJson(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

export function ConversationDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const detail = useQuery({
    queryKey: ['/api/admin/conversations', id],
    queryFn: () => api.get<ConversationDetail>(`/api/admin/conversations/${id}`),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/conversations" className="btn-secondary">
          <ArrowLeft className="h-4 w-4" /> Vissza
        </Link>
        <h2 className="text-xl font-semibold text-heading">Beszélgetés részletei</h2>
      </div>

      {detail.isLoading && <p className="text-sm text-slate-500">Betöltés…</p>}
      {detail.isError && (
        <p className="text-sm text-rose-600">Hiba: {String(detail.error)}</p>
      )}

      {detail.data && (
        <>
          <div className="card grid grid-cols-2 gap-4 p-4 text-sm sm:grid-cols-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Időpont</p>
              <p className="mt-1 font-medium">{formatDate(detail.data.occurredAt)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Deployment</p>
              <p className="mt-1 font-mono text-xs">{detail.data.deploymentSlug}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Modell</p>
              <p className="mt-1 font-mono text-xs">{detail.data.modelName}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Állapot</p>
              <p className="mt-1">
                <StatusBadge label={detail.data.status} tone={STATUS_TONE[detail.data.status]} />
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">NER időtartam</p>
              <p className="mt-1 font-medium tabular-nums">{formatMs(detail.data.nerMs)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">OCR időtartam</p>
              <p className="mt-1 font-medium tabular-nums">{formatMs(detail.data.ocrMs)}</p>
            </div>
          </div>

          <div className="card p-4">
            <Section title={`Felismert entitások (${detail.data.entities.length})`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-4 py-2">Típus</th>
                      <th className="px-4 py-2">Érték</th>
                      <th className="px-4 py-2">Placeholder</th>
                      <th className="px-4 py-2">Pontszám</th>
                      <th className="px-4 py-2">Forrás</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {detail.data.entities.map((e, i) => (
                      <tr key={`${e.placeholder}-${i}`}>
                        <td className="px-4 py-2 font-medium">{e.type}</td>
                        <td className="px-4 py-2 font-mono text-xs text-slate-600">{e.value}</td>
                        <td className="px-4 py-2 font-mono text-xs text-brand-700">{e.placeholder}</td>
                        <td className="px-4 py-2 tabular-nums">{e.score.toFixed(2)}</td>
                        <td className="px-4 py-2 text-xs text-slate-500">{e.source}</td>
                      </tr>
                    ))}
                    {detail.data.entities.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-4 text-center text-slate-500">
                          Nincs felismert entitás.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Section>
          </div>

          <div className="card space-y-2 p-4">
            <Section title="Eredeti kérés (csere előtt — amit a kliens küldött)">
              <Payload text={detail.data.requestPayload} />
            </Section>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="card space-y-2 p-4">
              <Section title="Kicserélt payload (LLM-nek küldve)">
                <Payload text={detail.data.swappedPayload} />
              </Section>
            </div>
            <div className="card space-y-2 p-4">
              <Section title="Nyers LLM-válasz (szöveg)">
                <Payload text={detail.data.rawResponse} />
              </Section>
            </div>
          </div>

          <div className="card space-y-2 p-4">
            <Section title="Nyers LLM-válasz (teljes JSON, kicserélt)">
              <Payload
                text={
                  detail.data.rawResponseJson
                    ? prettyJson(detail.data.rawResponseJson)
                    : '(nincs adat — a deploy előtti vagy responses-streaming turn)'
                }
              />
            </Section>
          </div>

          <div className="card space-y-2 p-4">
            <Section title="Végső, visszafordított válasz">
              <Payload text={detail.data.reversedResponse} />
            </Section>
          </div>
        </>
      )}
    </div>
  );
}
